import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Label,
  LabelList,
} from "recharts";
import { PRIORITY_COLORS, STATUS_COLORS } from "../../../config/constants";
import { formatDate } from "../../../config/utils";
import Loader from "../../../components/Loader";
import FilterChip from "../../../components/FilterChip";
import Pagination, { usePagination } from "../../../components/Pagination";
import StatusPill from "../../../components/StatusPill";
import EmptyState from "../../../components/EmptyState";

// ── Crisp SVG icons (consistent across all OS/browsers unlike emoji) ──

// ── Count-up animation hook ──
function useCountUp(target, duration = 750) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const to = typeof target === "number" ? target : 0;
    cancelAnimationFrame(rafRef.current);
    const from = fromRef.current;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setCurrent(Math.round(from + (to - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return current;
}

// ── Helper: time since a Date ──
function timeAgo(date) {
  if (!date) return null;
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// ── KPI card with SVG icon + animated number ──
function SummaryCard({ title, value, onClick, success, danger, icon }) {
  const isPercent = typeof value === "string" && value.endsWith("%");
  const isDash = value === "—" || value === null || value === undefined;
  const numericTarget = isDash
    ? 0
    : isPercent
      ? parseInt(value, 10)
      : typeof value === "number"
        ? value
        : 0;
  const animated = useCountUp(numericTarget);
  const displayValue = isDash ? "—" : isPercent ? `${animated}%` : animated;

  const accentColor = success ? "#16a34a" : danger ? "#dc2626" : "#0891b2";
  const cardBg = success
    ? "rgba(22,163,74,0.04)"
    : danger
      ? "rgba(220,38,38,0.04)"
      : "#fff";
  const borderColor = success
    ? "rgba(22,163,74,0.18)"
    : danger
      ? "rgba(220,38,38,0.18)"
      : "#eef0f4";
  const numColor = success ? "#16a34a" : danger ? "#dc2626" : "var(--text)";
  const iconColor = success ? "#16a34a" : danger ? "#dc2626" : "#0891b2";
  const iconBg = success
    ? "rgba(22,163,74,0.10)"
    : danger
      ? "rgba(220,38,38,0.10)"
      : "rgba(8,145,178,0.09)";

  return (
    <div
      className={`summary-card${onClick ? " clickable" : ""}${success ? " success" : ""}${danger ? " danger" : ""}`}
      onClick={onClick}
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderTop: `3px solid ${accentColor}`,
        borderRadius: 12,
        padding: "16px 18px 14px",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.15s, transform 0.15s",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={
        onClick
          ? (e) => {
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.10)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }
          : undefined
      }
      onMouseLeave={
        onClick
          ? (e) => {
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)";
              e.currentTarget.style.transform = "";
            }
          : undefined
      }
    >
      {icon && (
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: iconColor,
            marginBottom: 10,
          }}
        >
          {icon}
        </div>
      )}
      <div
        className="summary-card-value"
        style={{
          fontSize: 30,
          fontWeight: 800,
          color: numColor,
          lineHeight: 1,
          marginBottom: 7,
          letterSpacing: "-0.03em",
        }}
      >
        {displayValue}
      </div>
      <div
        className="summary-card-label"
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: success ? "var(--success-text)" : danger ? "var(--danger-text)" : "var(--text-2)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {title}
      </div>
      {onClick && (
        <div
          style={{
            fontSize: 11,
            color: accentColor,
            marginTop: 6,
            opacity: 0.65,
          }}
        >
          Click to view →
        </div>
      )}
    </div>
  );
}

// ── Custom chart tooltip (replaces Recharts default) ──
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #eef0f4",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        minWidth: 140,
        fontSize: 13,
      }}
    >
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: i < payload.length - 1 ? 5 : 0,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: p.fill || p.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: "var(--text-2)", flex: 1 }}>{p.name}</span>
          <span style={{ fontWeight: 700, color: "#0f1117" }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── SVG label in center of Recharts donut ──
function CenterLabel({ viewBox, total, label = "tasks" }) {
  const { cx, cy } = viewBox;
  return (
    <g>
      <text
        x={cx}
        y={cy - 7}
        textAnchor="middle"
        style={{ fontSize: 22, fontWeight: 800, fill: "#0f1117" }}
      >
        {total}
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        style={{ fontSize: 9, fill: "#9ca3af", letterSpacing: "0.06em" }}
      >
        {label.toUpperCase()}
      </text>
    </g>
  );
}

// ── Data label rendered inside each pie slice (skips slices < 5%) ──
function PieSliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) {
  if (!percent || percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 700, pointerEvents: "none" }}>
      {value}
    </text>
  );
}

// ── Personalized greeting banner with quick actions ──
function GreetingBanner({
  user,
  role,
  displaySummary,
  navigate,
  lastRefreshed,
  onRefresh,
  refreshing,
}) {
  const hour = new Date().getHours();
  const timeLabel =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const rawName = user?.name || user?.email || "there";
  const firstName = rawName.includes("@")
    ? rawName.split("@")[0]
    : rawName.split(" ")[0];
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const chips = [];
  if (displaySummary) {
    if (displaySummary.overdue > 0)
      chips.push({
        label: `${displaySummary.overdue} overdue`,
        color: "var(--danger)",
        bg: "#fff1f2",
      });
    if (displaySummary.inProgress > 0)
      chips.push({
        label: `${displaySummary.inProgress} in progress`,
        color: "var(--warn-text)",
        bg: "#fffbeb",
      });
    if (displaySummary.total > 0)
      chips.push({
        label: `${displaySummary.completionPct ?? 0}% done`,
        color: "var(--success-text)",
        bg: "#f0fdf4",
      });
  }

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f0f9ff 0%, #faf5ff 100%)",
        border: "1px solid rgba(8,145,178,0.13)",
        borderRadius: 16,
        padding: "20px 26px",
        marginBottom: 16,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: "-0.02em",
          }}
        >
          {timeLabel}, {cap(firstName)} 👋
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 6,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          {chips.map((c, i) => (
            <span
              key={i}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "2px 9px",
                borderRadius: 20,
                background: c.bg,
                color: c.color,
              }}
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {/* Refresh */}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            padding: "7px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
            border: "1px solid var(--border-strong)",
            background: "#fff",
            color: refreshing ? "#9ca3af" : "#4b5563",
            cursor: refreshing ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
          title="Refresh dashboard data"
        >
          <span
            style={{
              display: "inline-block",
              animation: refreshing ? "spin 0.8s linear infinite" : "none",
            }}
          >
            ↻
          </span>
          {lastRefreshed ? timeAgo(lastRefreshed) : "Refresh"}
        </button>
      </div>
    </div>
  );
}

function ChartSkeleton({ height = 260 }) {
  const pulse = "skeletonPulse 1.6s ease-in-out infinite";
  return (
    <div className="glass-card" style={{ padding: 24 }}>
      <div
        style={{
          height: 16,
          width: 130,
          borderRadius: 6,
          background: "var(--border-strong)",
          marginBottom: 16,
          animation: pulse,
        }}
      />
      <div
        style={{
          height,
          borderRadius: 10,
          background: "#f1f2f6",
          animation: pulse,
        }}
      />
    </div>
  );
}

export default function DashboardPage() {
  const { refreshKey } = useOutletContext();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [period, setPeriod] = useState("");
  const [error, setError] = useState("");
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const navigate = useNavigate();

  // Build query string for task drill-down links, carrying active project + dept + period filters
  function tasksUrl(extra = {}) {
    const p = new URLSearchParams();
    if (extra.status) p.set("status", extra.status);
    if (extra.overdue) p.set("overdue", "1");
    // dept from table row click takes priority; otherwise use the active dept filter
    const deptId = extra.dept || selectedDept;
    if (deptId) p.set("dept", deptId);
    if (projectId) p.set("projectId", projectId);
    if (period) p.set("period", period);
    const qs = p.toString();
    return `/tasks${qs ? `?${qs}` : ""}`;
  }

  const user = getUser();
  const role = user?.role || "Staff";
  const canSeeDepts =
    role === "CXO" || role === "Admin" || role === "Director";

  const [deptStats, setDeptStats] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [deptStatsError, setDeptStatsError] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [activeTasksPage, setActiveTasksPage] = useState(1);
  const [deptPage, setDeptPage] = useState(1);

  useEffect(() => {
    apiFetch("/api/projects")
      .then((r) => r.json())
      .then((d) => setProjects(Array.isArray(d) ? d : []))
      .catch(() => {});
    apiFetch("/api/departments")
      .then((r) => r.json())
      .then((d) => setAllDepts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const hasLoadedOnce = useRef(false);

  const loadAll = useCallback(async () => {
    setLoadingTasks(true);
    setLoadingSummary(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (projectId) params.set("projectId", projectId);
      if (period) params.set("period", period);
      const qs = params.toString() ? "?" + params.toString() : "";

      // Append a high limit so the dashboard gets all tasks for the selected period
      const tasksParams = new URLSearchParams(params);
      tasksParams.set("limit", "500");
      const tasksQs = "?" + tasksParams.toString();
      let tasksEndpoint =
        role === "Staff"
          ? `/api/tasks/my${tasksQs}`
          : `/api/tasks/visible${tasksQs}`;

      const [tRes, sRes] = await Promise.all([
        apiFetch(tasksEndpoint),
        apiFetch(`/api/dashboard/summary${qs}`),
      ]);

      if (!tRes.ok || !sRes.ok) throw new Error("API failed");
      const [t, s] = await Promise.all([tRes.json(), sRes.json()]);
      setTasks(Array.isArray(t) ? t : t.items || []);
      setSummary(s);
      setLastRefreshed(new Date());
      hasLoadedOnce.current = true;

      if (canSeeDepts) {
        setLoadingDepts(true);
        setDeptStatsError("");
        try {
          const deptStatsParams = new URLSearchParams();
          if (projectId) deptStatsParams.set("projectId", projectId);
          if (period) deptStatsParams.set("period", period);
          const deptStatsQs = deptStatsParams.toString() ? `?${deptStatsParams.toString()}` : "";
          const dRes = await apiFetch(`/api/departments/stats${deptStatsQs}`);
          if (dRes.ok) {
            const depts = await dRes.json();
            setDeptStats(Array.isArray(depts) ? depts : []);
          } else {
            setDeptStatsError("Could not load department stats.");
          }
        } catch {
          setDeptStatsError("Could not load department stats.");
        } finally {
          setLoadingDepts(false);
        }
      }
    } catch {
      setError("Failed to load dashboard data.");
    } finally {
      setLoadingTasks(false);
      setLoadingSummary(false);
    }
  }, [role, projectId, period, canSeeDepts]);

  useEffect(() => {
    loadAll();
  }, [loadAll, refreshKey]);

  const filteredTasks = useMemo(() => {
    if (!selectedDept) return tasks;
    // Include tasks from sub-departments when a parent dept is selected
    const subDeptIds = new Set([
      selectedDept,
      ...allDepts.filter(d => d.parentDepartmentId === selectedDept).map(d => d.id),
    ]);
    return tasks.filter(
      (t) => subDeptIds.has(String(t.assignedToUserDepartmentId)),
    );
  }, [tasks, selectedDept, allDepts]);

  const displaySummary = useMemo(() => {
    if (!selectedDept || !summary) return summary;
    const now = new Date();
    const open = filteredTasks.filter((t) => t.status === "Open").length;
    const inProgress = filteredTasks.filter(
      (t) => t.status === "InProgress",
    ).length;
    const hold = filteredTasks.filter((t) => t.status === "Hold").length;
    const underValidation = filteredTasks.filter(
      (t) => t.status === "UnderValidation",
    ).length;
    const completed = filteredTasks.filter(
      (t) => t.status === "Completed",
    ).length;
    const overdue = filteredTasks.filter(
      (t) => t.status !== "Completed" && t.dueDate && new Date(t.dueDate) < now,
    ).length;
    const total = filteredTasks.length;
    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return {
      total,
      open,
      inProgress,
      hold,
      underValidation,
      completed,
      overdue,
      completionPct,
      onTimePct: null,
      closed: completed,
    };
  }, [selectedDept, filteredTasks, summary]);

  const allActiveTasks = useMemo(
    () => filteredTasks.filter((t) => t.status !== "Completed"),
    [filteredTasks],
  );

  const filteredDeptStats = useMemo(
    () =>
      [...deptStats]
        .filter((d) => {
          // Never show sub-department rows in the summary table
          if (d.parentDepartmentId) return false;
          // If a dept is selected in the filter, show only that row
          if (selectedDept) return d.id === selectedDept;
          return true;
        })
        .sort((a, b) => b.open - a.open),
    [deptStats, selectedDept],
  );

  // On-time closure %: among ALL completed tasks, those with a dueDate that
  // was met count as on-time; completed tasks with no dueDate are excluded
  // from the denominator so they don't skew the metric either way.
  const onTimePct = useMemo(() => {
    const completed = filteredTasks.filter((t) => t.status === "Completed");
    const withDueDate = completed.filter((t) => t.dueDate);
    if (!withDueDate.length) return null;
    const onTime = withDueDate.filter(
      (t) => t.completedAt && new Date(t.completedAt) <= new Date(t.dueDate),
    ).length;
    return Math.round((onTime / withDueDate.length) * 100);
  }, [filteredTasks]);

  // Dept ranking by completion % (higher = better rank)
  const deptRanking = useMemo(() => {
    const sorted = [...filteredDeptStats]
      .filter((d) => d.totalTasks > 0)
      .sort((a, b) => b.closed / b.totalTasks - a.closed / a.totalTasks);
    const map = {};
    sorted.forEach((d, i) => {
      map[d.id] = i + 1;
    });
    return map;
  }, [filteredDeptStats]);

  // Sort departments: gold (rank 1) → silver (rank 2) → bronze (rank 3) → unranked
  const sortedDeptStats = useMemo(() => {
    return [...filteredDeptStats].sort((a, b) => {
      const rankA = deptRanking[a.id] ?? 999;
      const rankB = deptRanking[b.id] ?? 999;
      return rankA - rankB;
    });
  }, [filteredDeptStats, deptRanking]);

  const ACTIVE_PAGE_SIZE = 8;
  const DEPT_PAGE_SIZE = 10;

  const {
    totalPages: activeTasksTotalPages,
    safePage: safeActivePage,
    paginated: paginateActive,
  } = usePagination(allActiveTasks, ACTIVE_PAGE_SIZE);
  const {
    totalPages: deptTotalPages,
    safePage: safeDeptPage,
    paginated: paginateDepts,
  } = usePagination(sortedDeptStats, DEPT_PAGE_SIZE);

  const currentActiveTasksPage = safeActivePage(activeTasksPage);
  const activeTasks = paginateActive(currentActiveTasksPage);
  const currentDeptPage = safeDeptPage(deptPage);
  const pagedDeptStats = paginateDepts(currentDeptPage);

  // Reset pages when filters change
  useEffect(() => {
    setActiveTasksPage(1);
  }, [selectedDept, projectId, period]);
  useEffect(() => {
    setDeptPage(1);
  }, [selectedDept]);

  const statusChartData = useMemo(() => {
    const counts = {
      Open: 0,
      InProgress: 0,
      Hold: 0,
      UnderValidation: 0,
      Completed: 0,
    };
    filteredTasks.forEach((t) => {
      if (counts[t.status] !== undefined) counts[t.status]++;
    });
    return [
      { name: "Open", value: counts.Open, color: STATUS_COLORS.Open },
      {
        name: "In Progress",
        value: counts.InProgress,
        color: STATUS_COLORS.InProgress,
      },
      { name: "On Hold", value: counts.Hold, color: STATUS_COLORS.Hold },
      {
        name: "Under Validation",
        value: counts.UnderValidation,
        color: STATUS_COLORS.UnderValidation,
      },
      {
        name: "Completed",
        value: counts.Completed,
        color: STATUS_COLORS.Completed,
      },
    ].filter((d) => d.value > 0);
  }, [filteredTasks]);

  const priorityChartData = useMemo(() => {
    const counts = { High: 0, Medium: 0, Low: 0 };
    filteredTasks.forEach((t) => {
      if (counts[t.priority] !== undefined) counts[t.priority]++;
    });
    return [
      { name: "High", value: counts.High, color: PRIORITY_COLORS.High },
      { name: "Medium", value: counts.Medium, color: PRIORITY_COLORS.Medium },
      { name: "Low", value: counts.Low, color: PRIORITY_COLORS.Low },
    ].filter((d) => d.value > 0);
  }, [filteredTasks]);

  const userBarData = useMemo(() => {
    if (role === "Staff") return [];
    const map = {};
    filteredTasks.forEach((t) => {
      const name = t.assignedToUserName || "Unassigned";
      if (!map[name])
        map[name] = {
          name,
          Open: 0,
          InProgress: 0,
          Hold: 0,
          UnderValidation: 0,
          Completed: 0,
        };
      if (map[name][t.status] !== undefined) map[name][t.status]++;
    });
    return Object.values(map)
      .sort((a, b) => b.Open + b.InProgress - (a.Open + a.InProgress))
      .slice(0, 10);
  }, [filteredTasks, role]);

  return (
    <div>
      {/* Visually-hidden semantic headings for accessibility + test compatibility */}
      <h2
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {role === "CXO"
          ? "Organization Overview"
          : role === "Manager"
            ? "Team Overview"
            : "My Dashboard"}
      </h2>
      <p
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {"Live operational snapshot — " +
          new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
      </p>

      {/* ── Greeting banner ── */}
      <GreetingBanner
        user={user}
        role={role}
        displaySummary={displaySummary}
        navigate={navigate}
        lastRefreshed={lastRefreshed}
        onRefresh={loadAll}
        refreshing={loadingTasks || loadingSummary}
      />

      {/* ── Filter row ── */}
      {role !== "Staff" && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {canSeeDepts && allDepts.length > 0 && (
            <FilterChip
              label="All Departments"
              value={selectedDept}
              onChange={setSelectedDept}
              options={allDepts.filter(d => !d.parentDepartmentId).map((d) => ({ value: d.id, label: d.name }))}
            />
          )}
          <FilterChip
            label="All Projects"
            value={projectId}
            onChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
          <FilterChip
            label="All Time"
            value={period}
            onChange={setPeriod}
            options={[
              { value: "today", label: "Today" },
              { value: "week", label: "This Week" },
              { value: "month", label: "This Month" },
              { value: "quarter", label: "This Quarter" },
              { value: "halfyear", label: "This Half-Year" },
              { value: "year", label: "This Year" },
            ]}
          />
          {(selectedDept || projectId || period) && (
            <button
              onClick={() => {
                setSelectedDept("");
                setProjectId("");
                setPeriod("");
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                border: "1px solid #fecdd3",
                background: "var(--danger-bg)",
                color: "var(--danger-text)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 10,
            background: "var(--danger-soft)",
            border: "1px solid var(--danger-border)",
            color: "var(--danger-text)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ── Summary Cards ── */}
      {loadingSummary ? (
        <Loader.SummaryCards />
      ) : displaySummary ? (
        <div className="summary-grid" style={{ marginBottom: 24 }}>
          <SummaryCard
            icon="📋"
            title="Total"
            value={displaySummary.total}
            onClick={() => navigate(tasksUrl())}
          />
          <SummaryCard
            icon="⭕"
            title="Open"
            value={displaySummary.open}
            onClick={() => navigate(tasksUrl({ status: "Open" }))}
          />
          <SummaryCard
            icon="🔄"
            title="In Progress"
            value={displaySummary.inProgress}
            onClick={() => navigate(tasksUrl({ status: "InProgress" }))}
          />
          <SummaryCard
            icon="⏸"
            title="On Hold"
            value={displaySummary.hold}
            onClick={() => navigate(tasksUrl({ status: "Hold" }))}
          />
          <SummaryCard
            icon="🔍"
            title="Under Validation"
            value={displaySummary.underValidation}
            onClick={() => navigate(tasksUrl({ status: "UnderValidation" }))}
          />
          <SummaryCard
            icon="✅"
            title="Completed"
            value={displaySummary.completed}
            success
            onClick={() => navigate(tasksUrl({ status: "Completed" }))}
          />
          <SummaryCard
            icon="⚠️"
            title="Overdue"
            value={displaySummary.overdue}
            danger
            onClick={() => navigate(tasksUrl({ overdue: true }))}
          />
          <SummaryCard
            icon="📈"
            title="Completion %"
            value={`${displaySummary.completionPct ?? 0}%`}
            success={displaySummary.completionPct >= 70}
            danger={
              displaySummary.completionPct < 30 && displaySummary.total > 0
            }
          />
          <SummaryCard
            icon="⏱"
            title="On-Time %"
            value={onTimePct !== null ? `${onTimePct}%` : "—"}
            success={onTimePct >= 80}
            danger={onTimePct !== null && onTimePct < 50}
          />
        </div>
      ) : null}

      {/* ── Charts ── */}
      {loadingTasks && !hasLoadedOnce.current ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 24,
          }}
        >
          <ChartSkeleton height={220} />
          <ChartSkeleton height={220} />
        </div>
      ) : tasks.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 24,
            opacity: loadingTasks ? 0.5 : 1,
            transition: "opacity 0.2s",
          }}
        >
          {/* Status Donut */}
          <div className="glass-card" style={{ padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
              Status Overview
            </div>
            <div
              style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 14 }}
            >
              Tasks by current status
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={58}
                  outerRadius={88}
                  paddingAngle={3}
                  dataKey="value"
                  label={PieSliceLabel}
                  labelLine={false}
                >
                  {statusChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                  <Label
                    content={
                      <CenterLabel total={filteredTasks.length} label="tasks" />
                    }
                    position="center"
                  />
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{ fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Priority Donut */}
          <div className="glass-card" style={{ padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
              Priority Distribution
            </div>
            <div
              style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 14 }}
            >
              Tasks by priority level
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={priorityChartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={58}
                  outerRadius={88}
                  paddingAngle={3}
                  dataKey="value"
                  label={PieSliceLabel}
                  labelLine={false}
                >
                  {priorityChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                  <Label
                    content={
                      <CenterLabel total={filteredTasks.length} label="tasks" />
                    }
                    position="center"
                  />
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{ fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* User-wise stacked bar (non-Staff only) */}
      {loadingTasks && !hasLoadedOnce.current && role !== "Staff" ? (
        <ChartSkeleton height={Math.max(200, 5 * 42)} />
      ) : !loadingTasks || hasLoadedOnce.current ? (
        userBarData.length > 1 && (
          <div
            className="glass-card"
            style={{
              padding: 24,
              marginBottom: 24,
              opacity: loadingTasks ? 0.5 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
              Team Workload
            </div>
            <div
              style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 16 }}
            >
              Top {userBarData.length} members by active task count
            </div>
            <ResponsiveContainer
              width="100%"
              height={Math.max(200, userBarData.length * 42)}
            >
              <BarChart
                data={userBarData}
                layout="vertical"
                margin={{ left: 10, right: 20, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                  width={110}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="Open" name="Open" stackId="a" fill="#6b7280">
                  <LabelList dataKey="Open" position="inside" style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }} formatter={(v) => v > 0 ? v : ""} />
                </Bar>
                <Bar dataKey="InProgress" name="In Progress" stackId="a" fill="#f59e0b">
                  <LabelList dataKey="InProgress" position="inside" style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }} formatter={(v) => v > 0 ? v : ""} />
                </Bar>
                <Bar dataKey="Hold" name="On Hold" stackId="a" fill="#eab308">
                  <LabelList dataKey="Hold" position="inside" style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }} formatter={(v) => v > 0 ? v : ""} />
                </Bar>
                <Bar dataKey="UnderValidation" name="Under Validation" stackId="a" fill="#8b5cf6">
                  <LabelList dataKey="UnderValidation" position="inside" style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }} formatter={(v) => v > 0 ? v : ""} />
                </Bar>
                <Bar dataKey="Completed" name="Completed" stackId="a" fill="#16a34a" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="Completed" position="inside" style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }} formatter={(v) => v > 0 ? v : ""} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )
      ) : null}

      {/* ── Department Tasks (Admin / Director / CXO only) ── */}
      {canSeeDepts && (
        <div className="glass-card" style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                Department's Task Summary
              </div>
              <div
                style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}
              >
                Ranked by completion rate · 🥇 top performer
              </div>
            </div>
            {loadingDepts && <Loader size="sm" inline />}
          </div>

          {!loadingDepts && deptStatsError && (
            <div
              style={{
                padding: 16,
                fontSize: 13,
                color: "var(--danger-text)",
                background: "var(--danger-soft)",
                borderBottom: "1px solid var(--danger-border)",
              }}
            >
              {deptStatsError}
            </div>
          )}
          {!loadingDepts &&
            !deptStatsError &&
            filteredDeptStats.length === 0 && (
              <div
                style={{ padding: 24, color: "var(--text-3)", fontSize: 13 }}
              >
                No departments configured yet. Go to Departments to set them up.
              </div>
            )}

          {!loadingDepts && filteredDeptStats.length > 0 && (
            <div style={{ padding: "16px 24px", display: "grid", gap: 14 }}>
              {pagedDeptStats.map((d) => {
                const pct =
                  d.totalTasks > 0
                    ? Math.round((d.closed / d.totalTasks) * 100)
                    : 0;
                return (
                  <div
                    key={d.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 80px 80px 80px 80px 140px",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      {deptRanking[d.id] === 1 && (
                        <span
                          title="Top ranked department"
                          style={{ fontSize: 20, lineHeight: 1 }}
                        >
                          🥇
                        </span>
                      )}
                      {deptRanking[d.id] === 2 && (
                        <span
                          title="2nd ranked department"
                          style={{ fontSize: 20, lineHeight: 1 }}
                        >
                          🥈
                        </span>
                      )}
                      {deptRanking[d.id] === 3 && (
                        <span
                          title="3rd ranked department"
                          style={{ fontSize: 20, lineHeight: 1 }}
                        >
                          🥉
                        </span>
                      )}
                      {d.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-3)",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontWeight: 600,
                          color: "var(--text-2)",
                          fontSize: 14,
                        }}
                      >
                        {d.totalTasks}
                      </span>
                      Total
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-3)",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontWeight: 600,
                          color: "var(--brand)",
                          fontSize: 14,
                        }}
                      >
                        {d.open}
                      </span>
                      Open
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-3)",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontWeight: 600,
                          color: "var(--danger)",
                          fontSize: 14,
                          // FIX BUG-5: pass dept ID so TasksPage can pre-filter to this dept
                          cursor: d.overdue > 0 ? "pointer" : "default",
                        }}
                        onClick={() =>
                          d.overdue > 0 &&
                          navigate(tasksUrl({ overdue: true, dept: d.id }))
                        }
                        title={
                          d.overdue > 0
                            ? "Click to view overdue tasks for this department"
                            : ""
                        }
                      >
                        {d.overdue}
                      </span>
                      Overdue
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-3)",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontWeight: 600,
                          color: "var(--success)",
                          fontSize: 14,
                          // FIX BUG-5: Closed count also navigates with dept filter
                          cursor: d.closed > 0 ? "pointer" : "default",
                        }}
                        onClick={() =>
                          d.closed > 0 &&
                          navigate(tasksUrl({ status: "Completed", dept: d.id }))
                        }
                        title={
                          d.closed > 0
                            ? "Click to view closed tasks for this department"
                            : ""
                        }
                      >
                        {d.closed}
                      </span>
                      Closed
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 4,
                          fontSize: 11,
                          color: "var(--text-3)",
                        }}
                      >
                        <span>{d.name.slice(0, 12)}</span>
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              pct >= 70
                                ? "#16a34a"
                                : pct >= 40
                                  ? "#d97706"
                                  : "var(--text-2)",
                          }}
                        >
                          {pct}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: 6,
                          borderRadius: 999,
                          background: "var(--surface-2)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${pct}%`,
                            borderRadius: 999,
                            background:
                              pct >= 70
                                ? "#16a34a"
                                : pct >= 40
                                  ? "#f59e0b"
                                  : "#3b82f6",
                            transition: "width 0.5s ease",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!loadingDepts && filteredDeptStats.length > 0 && (
            <Pagination
              page={currentDeptPage}
              totalPages={deptTotalPages}
              onPageChange={(p) => setDeptPage(safeDeptPage(p))}
              total={filteredDeptStats.length}
              pageSize={DEPT_PAGE_SIZE}
            />
          )}
        </div>
      )}

      {/* ── Active Tasks ── */}
      <div
        className="glass-card"
        style={{ overflow: "hidden", marginTop: 24 }}
      >
        <div
          style={{
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {role === "Staff" ? "My Active Tasks" : "Active Tasks"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
              {allActiveTasks.length} task{allActiveTasks.length !== 1 ? "s" : ""} not yet completed
              {allActiveTasks.filter((t) => t.status === "Hold").length > 0 && (
                <span style={{ marginLeft: 6, color: "var(--warn-text)", fontWeight: 500 }}>
                  · {allActiveTasks.filter((t) => t.status === "Hold").length} on hold
                </span>
              )}
            </div>
          </div>
          <button className="btn-ghost" onClick={() => navigate(tasksUrl())} style={{ fontSize: 13 }}>
            View all →
          </button>
        </div>

        <div className="tasks-table">
          <div className="tasks-row header">
            <div>Task</div>
            <div>Assignee</div>
            <div>Due Date</div>
            <div>Status</div>
          </div>
          {loadingTasks ? (
            <Loader.TableRows rows={4} />
          ) : activeTasks.length === 0 ? (
            <EmptyState icon="✓" title="No active tasks right now" />
          ) : (
            activeTasks.map((task) => (
              <div key={task.id} className="tasks-row" onClick={() => navigate(`/tasks/${task.id}`)}>
                <div className="task-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      flexShrink: 0, width: 7, height: 7, borderRadius: "50%",
                      background: task.priority === "High" ? "#dc2626" : task.priority === "Medium" ? "#f59e0b" : "#16a34a",
                      display: "inline-block",
                    }}
                    title={`${task.priority || "Normal"} priority`}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {task.title}
                  </span>
                </div>
                <div className="assignee">
                  <div className="avatar">{(task.assignedToUserName || "?")[0].toUpperCase()}</div>
                  {task.assignedToUserName || "Unassigned"}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: task.dueDate && new Date(task.dueDate) < new Date() ? 600 : 400,
                    color: task.dueDate && new Date(task.dueDate) < new Date() ? "#dc2626" : "var(--text-2)",
                  }}
                >
                  {task.dueDate && new Date(task.dueDate) < new Date() && <span style={{ marginRight: 3 }}>⚠</span>}
                  {formatDate(task.dueDate)}
                </div>
                <div><StatusPill status={task.status} /></div>
              </div>
            ))
          )}
        </div>
        <Pagination
          page={currentActiveTasksPage}
          totalPages={activeTasksTotalPages}
          onPageChange={(p) => setActiveTasksPage(safeActivePage(p))}
          total={allActiveTasks.length}
          pageSize={ACTIVE_PAGE_SIZE}
        />
      </div>
    </div>
  );
}
